import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Input, LoadingSpinner } from '../components';
import { authService } from '../services';

/**
 * ForgotPasswordPage
 * Design: Black background, white form, minimalist
 * Purpose: Request password reset email
 */
export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleChange = (e) => {
    setEmail(e.target.value);
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const result = await authService.forgotPassword(email);

      if (result.success) {
        setIsSuccess(true);
      } else {
        setError(result.error || 'Failed to send reset email. Please try again.');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex flex-col items-center justify-center p-4 md:p-8">
      {/* Back to login */}
      <Link
        to="/login"
        className="absolute top-8 left-8 font-mono text-sm uppercase tracking-wider text-gray-400 hover:text-white transition-colors"
      >
        &larr; Back to Login
      </Link>

      {/* Form container */}
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="font-mono text-3xl md:text-4xl font-bold uppercase tracking-wider text-white mb-4">
            Lost Access?
          </h1>
          <p className="font-mono text-sm uppercase tracking-wider text-gray-400">
            Reset your credentials
          </p>
        </div>

        {/* Success state */}
        {isSuccess ? (
          <div className="bg-white p-8 md:p-12">
            <div className="text-center">
              {/* Success icon */}
              <div className="w-16 h-16 mx-auto mb-6 border-4 border-black flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-black"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={3}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>

              <h2 className="font-mono text-xl font-bold uppercase tracking-wider text-black mb-4">
                Check Your Email
              </h2>
              <p className="font-mono text-sm text-gray-600 mb-8">
                We've sent password reset instructions to{' '}
                <span className="font-bold text-black">{email}</span>
              </p>

              <Link
                to="/login"
                className="font-mono text-sm uppercase tracking-wider text-black hover:underline"
              >
                Return to Login
              </Link>
            </div>
          </div>
        ) : (
          /* Form */
          <form onSubmit={handleSubmit} className="bg-white p-8 md:p-12">
            {/* Error message */}
            {error && (
              <div className="mb-6 p-4 bg-red-50 border-2 border-red-600">
                <p className="font-mono text-sm text-red-600 uppercase tracking-wider">
                  {error}
                </p>
              </div>
            )}

            {/* Instructions */}
            <p className="font-mono text-sm text-gray-600 mb-6">
              Enter your email address and we'll send you a link to reset your password.
            </p>

            {/* Email field */}
            <div className="mb-8">
              <Input
                label="Email"
                id="email"
                name="email"
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={handleChange}
                required
                disabled={isLoading}
              />
            </div>

            {/* Submit button */}
            <Button
              type="submit"
              variant="primary"
              fullWidth
              disabled={isLoading || !email}
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <LoadingSpinner size="sm" />
                  Sending...
                </span>
              ) : (
                'Send Reset Link'
              )}
            </Button>
          </form>
        )}

        {/* Login link */}
        <div className="mt-8 text-center">
          <p className="font-mono text-sm uppercase tracking-wider text-gray-400">
            Remember your password?{' '}
            <Link
              to="/login"
              className="text-white hover:underline"
            >
              Login
            </Link>
          </p>
        </div>
      </div>

      {/* Decorative corners */}
      <div className="absolute top-8 right-8 w-16 h-16 border-r-2 border-t-2 border-white opacity-20" />
      <div className="absolute bottom-8 left-8 w-16 h-16 border-l-2 border-b-2 border-white opacity-20" />
      <div className="absolute bottom-8 right-8 w-16 h-16 border-r-2 border-b-2 border-white opacity-20" />
    </div>
  );
}
